/** A Copilot model choice, as the token response carries it. */
export interface EmbeddedAiModelOption {
  key: string;
  label: string;
  description: string;
  internal: boolean;
}

export interface EmbeddedPushPullFeatures {
  organic: boolean;
  prismatic: boolean;
  native_parametric: boolean;
}

/**
 * The signed-in user's fields from the token response that an embedded
 * editor needs for its role and subscription checks. Never holds a token.
 */
export interface EmbeddedAuthProfile {
  email: string;
  role: string;
  permissions?: string[];
  manufacturing_features?: string[];
  cad_generation_features?: string[];
  platform_features?: string[];
  load_case_features?: string[];
  constraints_features?: string[];
  cad_download_features?: string[];
  plugin_features?: string[];
  push_pull_features?: EmbeddedPushPullFeatures;
  subscription_status?: string;
  subscription_seat_active?: boolean;
  ai_model_options?: EmbeddedAiModelOption[];
  process_comparison_enabled?: boolean;
}

/** What the cabinet hands an embedded editor; `expiresAt` is in ms since the epoch. */
export interface EmbeddedAuthPayload {
  token: string;
  expiresAt: number;
  profile: EmbeddedAuthProfile;
}

/** Any object carrying the profile fields, such as a token response. */
export type EmbeddedAuthProfileSource = { email: string; role: string } & {
  [K in keyof EmbeddedAuthProfile]?: unknown;
};

const STRING_LIST_KEYS = [
  'permissions',
  'manufacturing_features',
  'cad_generation_features',
  'platform_features',
  'load_case_features',
  'constraints_features',
  'cad_download_features',
  'plugin_features',
] as const;

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isPushPull(value: unknown): value is EmbeddedPushPullFeatures {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record['organic'] === 'boolean' &&
    typeof record['prismatic'] === 'boolean' &&
    typeof record['native_parametric'] === 'boolean'
  );
}

function isAiModelOption(value: unknown): value is EmbeddedAiModelOption {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record['key'] === 'string' &&
    typeof record['label'] === 'string' &&
    typeof record['description'] === 'string' &&
    typeof record['internal'] === 'boolean'
  );
}

/** True for an object with a string email and role. */
export function isEmbeddedAuthProfileSource(value: unknown): value is EmbeddedAuthProfileSource {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record['email'] === 'string' && typeof record['role'] === 'string';
}

/** Copies only the known profile fields of the right shape. */
export function pickEmbeddedProfile(source: EmbeddedAuthProfileSource): EmbeddedAuthProfile {
  const profile: EmbeddedAuthProfile = { email: source.email, role: source.role };
  for (const key of STRING_LIST_KEYS) {
    const value = source[key];
    if (isStringList(value)) profile[key] = [...value];
  }
  if (isPushPull(source.push_pull_features)) {
    profile.push_pull_features = { ...source.push_pull_features };
  }
  if (typeof source.subscription_status === 'string') {
    profile.subscription_status = source.subscription_status;
  }
  if (typeof source.subscription_seat_active === 'boolean') {
    profile.subscription_seat_active = source.subscription_seat_active;
  }
  if (typeof source.process_comparison_enabled === 'boolean') {
    profile.process_comparison_enabled = source.process_comparison_enabled;
  }
  const options = source.ai_model_options;
  if (Array.isArray(options) && options.every(isAiModelOption)) {
    profile.ai_model_options = options.map((option) => ({ ...option }));
  }
  return profile;
}
